import { useState } from 'react'
import { createUseStyles, useTheme } from 'react-jss'
import { DownOutlined, UpOutlined } from '@ant-design/icons'
import PropTypes from 'prop-types'
import { MOBILE_MEDIA_QUERY } from '../../lib/responsive'
import { MOBILE_HEADER_HEIGHT } from '../Layout/Header'

const useStyles = createUseStyles({
  card: {
    background: '#fff',
    border: '1px solid #f0f0f0',
    borderRadius: 8,
    padding: 24,
    marginBottom: 16,
    [MOBILE_MEDIA_QUERY]: {
      // 24px a side takes a seventh of a 375px screen away from the content.
      padding: 16,
      marginBottom: 12,
    },
  },
  cardCollapsed: {
    padding: '16px 24px',
    [MOBILE_MEDIA_QUERY]: {
      padding: 16,
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    // Padding rather than margin so the sticky header's own background covers
    // the gap; with a margin the body would scroll visibly through it.
    paddingBottom: 28,
    [MOBILE_MEDIA_QUERY]: {
      position: 'sticky',
      // Clear the app header, which is itself sticky at this breakpoint.
      top: MOBILE_HEADER_HEIGHT,
      zIndex: 2,
      // Opaque, or the body scrolls visibly through the pinned title. Taken
      // from the card so a custom card background still matches.
      backgroundColor: 'inherit',
    },
  },
  headerCollapsed: {
    paddingBottom: 0,
  },
  headerClickable: {
    cursor: 'pointer',
    userSelect: 'none',
    // Not `none`: this rule is emitted after `header`, so a transparent
    // background here would win and unstick the sticky header's backdrop.
    backgroundColor: 'inherit',
    border: 'none',
    // Reset the button's own padding per side rather than with the shorthand,
    // which — emitted after `header` — would otherwise flatten its paddingBottom
    // and leave the title sitting straight on top of the section body.
    paddingTop: 0,
    paddingLeft: 0,
    paddingRight: 0,
    width: '100%',
    font: 'inherit',
    textAlign: 'left',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    minWidth: 0,
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexShrink: 0,
  },
  icon: {
    fontSize: 18,
    color: ({ theme }) => theme.brandPrimary,
    display: 'flex',
    alignItems: 'center',
  },
  title: {
    fontSize: 16,
    fontWeight: 600,
    color: ({ theme }) => theme.textColor,
    margin: 0,
    lineHeight: '24px',
  },
  chevron: {
    fontSize: 12,
    color: '#999',
    transition: 'transform 0.2s',
    display: 'flex',
    alignItems: 'center',
  },
  body: {
    overflow: 'hidden',
    transition: 'max-height 0.25s ease, opacity 0.2s ease',
    maxHeight: 5000,
    opacity: 1,
  },
  bodyCollapsed: {
    maxHeight: 0,
    opacity: 0,
  },
})

export default function SectionCard({
  title = undefined,
  icon = undefined,
  extra = undefined,
  children = undefined,
  noPadding = false,
  collapsible = false,
  defaultCollapsed = false,
  onCollapseChange = undefined,
  style = {},
}) {
  const theme = useTheme()
  const classes = useStyles({ theme })
  const [collapsed, setCollapsed] = useState(defaultCollapsed)

  const isCollapsed = collapsible && collapsed

  return (
    <div
      className={`${classes.card}${isCollapsed ? ` ${classes.cardCollapsed}` : ''}`}
      style={{
        ...(noPadding ? { padding: 0 } : {}),
        ...style,
      }}
    >
      {title && !collapsible && (
        <div className={classes.header}>
          <div className={classes.titleRow}>
            {icon && <span className={classes.icon}>{icon}</span>}
            <h3 className={classes.title}>{title}</h3>
          </div>
          {extra && <div>{extra}</div>}
        </div>
      )}
      {title && collapsible && (
        <button
          type="button"
          className={`${classes.header}${isCollapsed ? ` ${classes.headerCollapsed}` : ''} ${classes.headerClickable}`}
          aria-expanded={!isCollapsed}
          onClick={() => setCollapsed(c => {
            const next = !c
            onCollapseChange?.(next)
            return next
          })}
        >
          <div className={classes.titleRow}>
            {icon && <span className={classes.icon}>{icon}</span>}
            <h3 className={classes.title}>{title}</h3>
          </div>
          <div className={classes.headerRight}>
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <div onClick={e => e.stopPropagation()}>
              {extra && <div>{extra}</div>}
            </div>
            <span className={classes.chevron}>
              {isCollapsed ? <DownOutlined /> : <UpOutlined />}
            </span>
          </div>
        </button>
      )}
      {!title && children && children}
      {title && (
        <div className={`${classes.body}${isCollapsed ? ` ${classes.bodyCollapsed}` : ''}`}>
          {children}
        </div>
      )}
    </div>
  )
}

SectionCard.propTypes = {
  title: PropTypes.node,
  icon: PropTypes.node,
  extra: PropTypes.node,
  children: PropTypes.node,
  noPadding: PropTypes.bool,
  collapsible: PropTypes.bool,
  defaultCollapsed: PropTypes.bool,
  onCollapseChange: PropTypes.func,
  style: PropTypes.shape(),
}
